<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next';

import { Button, type ButtonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import type { HTMLAttributes } from 'vue';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface Props {
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  class?: HTMLAttributes['class'];
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  disabled: false,
  loading: false,
  type: 'button',
  class: undefined,
});

defineEmits<{
  click: [event: MouseEvent];
}>();

const variantMap: Record<ButtonVariant, ButtonVariants['variant']> = {
  primary: 'default',
  secondary: 'secondary',
  outline: 'outline',
  ghost: 'ghost',
  destructive: 'destructive',
  link: 'link',
};

const sizeMap: Record<ButtonSize, ButtonVariants['size']> = {
  sm: 'sm',
  md: 'default',
  lg: 'lg',
};
</script>

<template>
  <Button
    :type="type"
    :variant="variantMap[variant]"
    :size="sizeMap[size]"
    :disabled="disabled || loading"
    :class="cn(props.class)"
    @click="$emit('click', $event)"
  >
    <Loader2 v-if="loading" class="animate-spin" />
    <slot />
  </Button>
</template>
